import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  ActionTabs,
  AppIcon,
  ConfirmDelete,
  ContentNugget,
  FullScreenLoading,
  NotFound404,
  NUGGET_CARD_WIDTH,
  addHttpIfMissing,
} from '@backrs_ui/common-ui';

import { dayBadge } from '@backrs_ui/common-ui/src/utils/dateTime.util';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  DetailHeader,
  detailHeaderChipSx,
  detailHeaderCompletePillSx,
} from '@backrs_ui/grow-ui/src/components/DetailHeader/DetailHeader';
import { NuggetCardItem } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardItem';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { StickyNoteBoard } from '@backrs_ui/grow-ui/src/components/StickyNotes/StickyNoteBoard';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import {
  StickerCardId,
  useToolCardById,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import {
  FEATURE_FLAGS,
  selectIsFeatureFlagEnabled,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useGetPlaylistByIdQuery,
  useGetTaskByIdQuery,
  useGetTasksForSuggestedPlaylistQuery,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.api';
import {
  PRIORITY_TASK_STATUS_CODES,
  PRIORITY_TYPES,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, Button, Chip, SxProps, Theme, Typography } from '@mui/material';

import { useAgentContext } from './TaskPageContainer.agentContext';
import { useGetTaskActionsAndProperties, useTaskLinkNugget } from '../hooks/tasks.hooks';
import { TaskReflection } from '../molecules/TaskReflection';
import { TEMPLATE_PLAYBOOK_QUERY_KEY } from '../utils/playlist.utils';
import { transformTaskContentsToNuggets } from '../utils/tasks.utils';

const STRINGS = appStrings.modules.priorities.taskPage;
const TASK_CARD_STRINGS = appStrings.modules.priorities.taskCard;

export const TaskPageContainer = () => {
  const { taskId: taskIdParam } = useParams<{ taskId: string }>();
  const taskId = Number(taskIdParam);
  const hasValidId = Number.isInteger(taskId) && taskId > 0;

  const navigate = useNavigate();
  const { getStickerCard } = useToolCardById({ navigate });
  const [searchParams] = useSearchParams();

  // Set when arriving from a template playbook: its tasks belong to the template's
  // author, so they resolve through the playbook rather than by id.
  const templatePlaylistId = Number(searchParams.get(TEMPLATE_PLAYBOOK_QUERY_KEY)) || null;

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const isStickyNotesEnabled = useAppSelector(
    selectIsFeatureFlagEnabled(FEATURE_FLAGS.STICKY_NOTES),
  );

  const {
    data: ownTask,
    isLoading: isLoadingOwnTask,
    isError: isOwnTaskError,
  } = useGetTaskByIdQuery(taskId, { skip: !hasValidId || !!templatePlaylistId });

  const {
    data: templateTasks,
    isLoading: isLoadingTemplateTasks,
    isError: isTemplateTasksError,
  } = useGetTasksForSuggestedPlaylistQuery(templatePlaylistId ?? 0, {
    skip: !templatePlaylistId,
  });

  const task = templatePlaylistId
    ? templateTasks?.find((candidate) => candidate.id === taskId)
    : ownTask;
  const isLoading = templatePlaylistId ? isLoadingTemplateTasks : isLoadingOwnTask;
  const isError = templatePlaylistId ? isTemplateTasksError : isOwnTaskError;
  const isTemplateTask = !!templatePlaylistId;

  const { data: playlist } = useGetPlaylistByIdQuery(
    { id: task?.playlist_id ?? 0 },
    { skip: !task?.playlist_id },
  );

  const { deleteTask, handleOpenEditTaskDialog, handleOpenNewTaskDialog, updateTask } =
    useGetTaskActionsAndProperties();

  const linkNugget = useTaskLinkNugget(task);
  // The playbook row shows a plain link beside attached content; the page does the same,
  // so a task whose only content is a link is not empty here.
  const attachedNuggets = task ? transformTaskContentsToNuggets(task) : [];
  const nuggets = linkNugget ? [...attachedNuggets, linkNugget] : attachedNuggets;

  usePageTitle(task?.title);
  useAgentContext(task, attachedNuggets);

  if (isLoading) {
    return <FullScreenLoading />;
  }

  if (!hasValidId || isError || !task) {
    return <NotFound404 />;
  }

  const handleNuggetClick = (nugget: ContentNugget) => {
    if (nugget.variant === 'tool' && nugget.tool_info) {
      const stickerCard = getStickerCard(nugget.tool_info.tool_id as StickerCardId);

      if (stickerCard?.onClick) {
        stickerCard.onClick();

        return;
      }
    }

    if (task.content_url) {
      window.open(addHttpIfMissing(task.content_url), '_blank');

      return;
    }

    handlePanelNuggetClick(nugget, navigate);
  };

  const completed = task.status_code === PRIORITY_TASK_STATUS_CODES.COMPLETED;

  const handleToggleComplete = async () => {
    try {
      await updateTask({
        id: task.id,
        status_code: completed
          ? PRIORITY_TASK_STATUS_CODES.NOT_STARTED
          : PRIORITY_TASK_STATUS_CODES.COMPLETED,
      }).unwrap();
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleDeleteTask = async () => {
    try {
      await deleteTask(task.id).unwrap();
    } catch (error) {
      console.error('Failed to delete task:', error);

      return;
    }

    setConfirmDeleteOpen(false);
    navigate(
      task.playlist_id
        ? APP_ROUTE.PLAYBOOK.replace(':playbookId', String(task.playlist_id))
        : APP_ROUTE.PRIORITIES,
    );
  };

  return (
    <Box sx={pageSx}>
      <ConfirmDelete
        openDialog={confirmDeleteOpen}
        setOpenDialog={setConfirmDeleteOpen}
        headerText={TASK_CARD_STRINGS.deleteTask}
        bodyText={TASK_CARD_STRINGS.deleteTaskConfirmBody}
        handleDelete={handleDeleteTask}
      />

      <DetailHeader
        id="task-page-header"
        badge={task.due_date ? dayBadge(task.due_date) : undefined}
        title={task.title}
        titleAdornment={
          task.priority ? (
            <Chip
              id="task-page-priority-chip"
              size="sm"
              variant="badge"
              label={`${PRIORITY_TYPES[task.priority]} ${STRINGS.priority}`}
              sx={detailHeaderChipSx}
            />
          ) : undefined
        }
        actions={
          <>
            {!isTemplateTask && (
              <Button
                id="task-page-complete-toggle"
                hierarchy="Secondary color"
                size="md"
                icon="Leading"
                onClick={handleToggleComplete}
                aria-label={STRINGS.toggleCompleteAria}
                aria-pressed={completed}
                startIcon={
                  <AppIcon
                    iconName={completed ? 'CheckCircle' : 'ClockFastForward'}
                    diameter="20px"
                    color="currentColor"
                  />
                }
                sx={completed ? detailHeaderCompletePillSx : undefined}
              >
                {completed ? STRINGS.complete : STRINGS.incomplete}
              </Button>
            )}

            {task.playlist_id && (
              <Button
                id="task-page-view-playbook-link"
                component={RouterLink}
                to={APP_ROUTE.PLAYBOOK.replace(
                  ':playbookId',
                  String(templatePlaylistId ?? task.playlist_id),
                )}
                hierarchy="Secondary gray"
                size="md"
              >
                {STRINGS.viewPlaybook}
              </Button>
            )}
          </>
        }
        secondaryActions={
          isTemplateTask ? undefined : (
            <ActionTabs
              borderColor="Colors/Border/border-primary"
              actions={[
                {
                  id: 'task-page-duplicate-button',
                  icon: 'Copy04',
                  onClick: () => handleOpenNewTaskDialog(playlist ?? null, false, task),
                  tooltipHoverText: STRINGS.duplicate,
                },
                {
                  id: 'task-page-delete-button',
                  icon: 'Trash01',
                  onClick: () => setConfirmDeleteOpen(true),
                  tooltipHoverText: STRINGS.delete,
                },
                {
                  id: 'task-page-edit-button',
                  icon: 'Pencil01',
                  onClick: () => handleOpenEditTaskDialog(task, false),
                  tooltipHoverText: STRINGS.edit,
                },
              ]}
            />
          )
        }
      />

      <Box sx={bodySx}>
        <Box sx={detailsColSx}>
          {task.description && (
            <Box sx={descriptionBlockSx}>
              <Typography variant="text-md-semibold" sx={sectionLabelSx}>
                {STRINGS.description}
              </Typography>
              <Typography variant="text-md-regular" sx={descriptionSx}>
                {task.description}
              </Typography>
            </Box>
          )}

          {!!task.tags?.length && (
            <Box sx={chipRowSx}>
              {task.tags.map((tag) => (
                <Chip
                  key={tag.id}
                  id={`task-page-tag-${tag.id}`}
                  size="md"
                  variant="pill"
                  label={tag.title}
                  sx={tagChipSx}
                />
              ))}
            </Box>
          )}
        </Box>

        {nuggets.length > 0 && (
          <Box sx={nuggetColSx}>
            {nuggets.map((nugget) => (
              <NuggetCardItem
                key={nugget.id}
                nugget={nugget}
                onClick={handleNuggetClick}
                hideActions
                fluid
              />
            ))}
          </Box>
        )}
      </Box>

      {!isTemplateTask && (
        <>
          <Box sx={dividerSx} aria-hidden />
          <Box sx={reflectionRowSx}>
            <Box sx={isStickyNotesEnabled ? reflectionColSx : fullWidthReflectionColSx}>
              <TaskReflection key={task.id} taskId={task.id} />
            </Box>
            {isStickyNotesEnabled && (
              <StickyNoteBoard
                key={`sticky-notes-${task.id}`}
                target={{ target_type: 'priorities_task', target_id: task.id }}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  // Sticky-note positions are measured from this box's top-left.
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
  containerType: 'inline-size',
  pb: backrs.spacing['spacing-2xl'],
});

// Unpositioned so note positions measure from the page; switches on the viewport, like the notes.
const reflectionRowSx: SxProps<Theme> = ({ backrs, breakpoints }) => ({
  display: 'flex',
  flexDirection: 'column',
  [breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-4xl'],
});

const reflectionColSx: SxProps<Theme> = ({ breakpoints }) => ({
  minWidth: 0,
  width: '100%',
  [breakpoints.up('sm')]: {
    flex: '0 1 70%',
  },
});

const fullWidthReflectionColSx: SxProps<Theme> = {
  minWidth: 0,
  width: '100%',
};

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderTop: `1px solid ${backrs.colors['Beige/950']}`,
});

const chipRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const tagChipSx: SxProps<Theme> = ({ backrs }) => ({
  padding: `${backrs.spacing['spacing-xxs']} 10px`,
  border: `1.5px solid ${backrs.colors['Gray/600']}`,
  '& .MuiChip-label': {
    color: backrs.colors['Gray/700'],
  },
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  '@container (min-width: 600px)': {
    flexDirection: 'row',
  },
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-2xl'],
  padding: backrs.spacing['spacing-xl'],
});

const detailsColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  flex: 1,
  minWidth: 0,
});

const nuggetColSx: SxProps<Theme> = {
  flexShrink: 0,
  width: '100%',
  // Beside the details column the card needs a definite width: it sizes itself with
  // `width: 100%`, which collapses to min-content against an auto-width flex item.
  '@container (min-width: 600px)': { width: NUGGET_CARD_WIDTH },
};

const descriptionBlockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const sectionLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  whiteSpace: 'pre-wrap',
});
